import { SEO_PAGE_KEYS, type SeoConfig } from '@klaro7/shared-types';

import { resolveSeoTemplate } from './seoUtils';
import {
  buildConsultantJsonLd,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  type JsonLdObject,
} from './jsonLd';
import type { PublicProfessional } from '@/features/professionals/services/professional-public.service';

export type AuditLevel = 'ok' | 'warn' | 'error';

export interface AuditFinding {
  level: AuditLevel;
  area: string;
  message: string;
}

const SCORE_ERROR_PENALTY = 12;
const SCORE_WARN_PENALTY = 3;

/** Health score 0-100: 100 - 12 per error - 3 per warning. */
export function computeSeoScore(findings: AuditFinding[]): number {
  let score = 100;
  for (const finding of findings) {
    if (finding.level === 'error') score -= SCORE_ERROR_PENALTY;
    else if (finding.level === 'warn') score -= SCORE_WARN_PENALTY;
  }
  return Math.min(100, Math.max(0, score));
}

const TITLE_MAX = 60;
const TITLE_MIN = 15;
const DESC_MAX = 160;
const DESC_MIN = 50;

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function checkTitle(findings: AuditFinding[], area: string, title: string | undefined, required: boolean) {
  if (!title) {
    if (required) findings.push({ level: 'error', area, message: 'Meta title mancante' });
    return;
  }
  if (title.length > TITLE_MAX) {
    findings.push({ level: 'warn', area, message: `Title di ${title.length} caratteri (consigliato ≤ ${TITLE_MAX}): Google potrebbe troncarlo` });
  } else if (title.length < TITLE_MIN) {
    findings.push({ level: 'warn', area, message: `Title molto corto (${title.length} caratteri)` });
  } else {
    findings.push({ level: 'ok', area, message: `Title ok (${title.length} caratteri)` });
  }
}

function checkDescription(findings: AuditFinding[], area: string, desc: string | undefined, required: boolean) {
  if (!desc) {
    if (required) findings.push({ level: 'error', area, message: 'Meta description mancante' });
    else findings.push({ level: 'warn', area, message: 'Description non impostata: usa il default del sito' });
    return;
  }
  if (desc.length > DESC_MAX) {
    findings.push({ level: 'warn', area, message: `Description di ${desc.length} caratteri (consigliato ≤ ${DESC_MAX}): verrà troncata in SERP` });
  } else if (desc.length < DESC_MIN) {
    findings.push({ level: 'warn', area, message: `Description corta (${desc.length} caratteri, consigliato ≥ ${DESC_MIN})` });
  } else {
    findings.push({ level: 'ok', area, message: `Description ok (${desc.length} caratteri)` });
  }
}

/** Validates a generated JSON-LD block: serializable + minimum schema.org shape. */
export function validateJsonLdBlock(area: string, block: JsonLdObject | null, enabled: boolean): AuditFinding[] {
  const findings: AuditFinding[] = [];
  if (!enabled) {
    findings.push({ level: 'warn', area, message: 'Disattivato: nessun dato strutturato emesso' });
    return findings;
  }
  if (!block) {
    findings.push({ level: 'error', area, message: 'Attivo ma non generabile (dati insufficienti)' });
    return findings;
  }
  try {
    JSON.parse(JSON.stringify(block));
  } catch {
    findings.push({ level: 'error', area, message: 'JSON-LD non serializzabile' });
    return findings;
  }
  if (block['@context'] !== 'https://schema.org') {
    findings.push({ level: 'error', area, message: '@context mancante o diverso da https://schema.org' });
  }
  if (!block['@type']) {
    findings.push({ level: 'error', area, message: '@type mancante' });
  }
  if (!block.name) {
    findings.push({ level: 'error', area, message: 'Proprietà "name" mancante' });
  }
  if (findings.length === 0) {
    findings.push({ level: 'ok', area, message: `${String(block['@type'])} valido` });
  }
  return findings;
}

/** Full config audit shown in the admin "Verifica" tab. */
export function auditSeoConfig(
  config: SeoConfig,
  sampleProfessional: PublicProfessional | undefined,
  origin: string
): AuditFinding[] {
  const findings: AuditFinding[] = [];

  // --- Global ---
  checkTitle(findings, 'Generale', config.global.defaultTitle, true);
  checkDescription(findings, 'Generale', config.global.defaultDescription, true);
  if (!config.global.defaultOgImage) {
    findings.push({ level: 'warn', area: 'Generale', message: 'Immagine OG di default assente: le condivisioni social usano il logo' });
  } else if (!isHttpUrl(config.global.defaultOgImage)) {
    findings.push({ level: 'error', area: 'Generale', message: 'Immagine OG di default: URL non valido' });
  }
  if (config.global.defaultKeywords.length === 0) {
    findings.push({ level: 'warn', area: 'Generale', message: 'Nessuna keyword di default' });
  }
  if (!config.global.titleSuffix) {
    findings.push({ level: 'warn', area: 'Generale', message: 'Suffisso titolo vuoto: i titoli non conterranno il brand' });
  }

  // --- Pages ---
  for (const key of SEO_PAGE_KEYS) {
    const page = config.pages[key] ?? {};
    const area = `Pagina ${key}`;
    if (page.noindex) {
      findings.push({ level: 'warn', area, message: 'noindex attivo: esclusa da Google e dalla sitemap' });
      continue;
    }
    checkTitle(findings, area, page.title || config.global.defaultTitle, true);
    checkDescription(findings, area, page.description, false);
    if (page.ogImage && !isHttpUrl(page.ogImage)) {
      findings.push({ level: 'error', area, message: 'Immagine OG: URL non valido' });
    }
  }

  // --- Consultant templates ---
  const area = 'Template consulenti';
  if (!config.consultant.titleTemplate.includes('{nome}')) {
    findings.push({ level: 'error', area, message: 'Il template titolo non contiene {nome}: tutte le schede avrebbero lo stesso titolo' });
  }
  if (sampleProfessional) {
    const profile = sampleProfessional.userId?.profile;
    const values = {
      nome: `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim(),
      titolo: sampleProfessional.title,
      specializzazioni: sampleProfessional.specializations.join(', '),
      bio: (sampleProfessional.profileDescription ?? '').replace(/<[^>]+>/g, ' ').trim(),
    };
    checkTitle(findings, area, resolveSeoTemplate(config.consultant.titleTemplate, values), true);
    checkDescription(findings, area, resolveSeoTemplate(config.consultant.descriptionTemplate, values), true);
  } else {
    findings.push({ level: 'warn', area, message: 'Nessun consulente pubblicato: impossibile testare i template su dati reali' });
  }

  // --- JSON-LD ---
  findings.push(
    ...validateJsonLdBlock('JSON-LD Organization', buildOrganizationJsonLd(config, origin), config.jsonLd.organizationEnabled),
    ...validateJsonLdBlock('JSON-LD WebSite', buildWebSiteJsonLd(config, origin), config.jsonLd.websiteEnabled),
  );
  if (config.jsonLd.consultantPersonEnabled && sampleProfessional) {
    findings.push(
      ...validateJsonLdBlock(
        'JSON-LD Person (consulente)',
        buildConsultantJsonLd(config, sampleProfessional, `${origin}/consultant/${sampleProfessional.id}`),
        true,
      ),
    );
  } else if (!config.jsonLd.consultantPersonEnabled) {
    findings.push({ level: 'warn', area: 'JSON-LD Person (consulente)', message: 'Disattivato: nessun dato strutturato emesso' });
  }
  findings.push({
    level: config.jsonLd.faqPageEnabled ? 'ok' : 'warn',
    area: 'JSON-LD FAQPage',
    message: config.jsonLd.faqPageEnabled
      ? 'Attivo: generato dalle domande della pagina FAQ'
      : 'Disattivato: nessun dato strutturato emesso',
  });

  for (const [i, url] of config.jsonLd.organizationSameAs.entries()) {
    if (!isHttpUrl(url)) {
      findings.push({ level: 'error', area: 'JSON-LD Organization', message: `Profilo social #${i + 1}: URL non valido (${url})` });
    }
  }

  return findings;
}
